import React, { useState } from 'react';
import { X, Clock, Flame, Calendar, Ban, Upload, ChevronRight, Eye, Share2, Edit } from 'lucide-react';
import { Challenge } from '../../types/challenges';
import { auth } from '../../lib/firebase';
import { ChallengeCompletionModal } from './ChallengeCompletionModal';
import { ChallengeResultsModal } from './ChallengeResultsModal';
import { EditChallengeModal } from './EditChallengeModal';
import { completeChallenge } from '../../lib/store';

interface ChallengeDetailsModalProps {
  challenge: Challenge;
  onClose: () => void;
  onJoin: (challengeId: string) => Promise<void>;
  onQuit: (challengeId: string) => Promise<void>;
  onComplete?: (challenge: Challenge) => void;
  userParticipating: boolean;
}

export function ChallengeDetailsModal({ 
  challenge, 
  onClose, 
  onJoin,
  onQuit,
  onComplete,
  userParticipating 
}: ChallengeDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [isParticipating, setIsParticipating] = useState(userParticipating);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = auth.currentUser?.email === 'breadflowbybadabec@gmail.com';
  const userParticipant = auth.currentUser
    ? challenge.participants.find(p => p.userId === auth.currentUser?.uid)
    : null;
  const isCompleted = userParticipant?.completed || false;

  const handleCompleteSubmit = async (imageFile: File, notes: string) => {
    try {
      setLoading(true);
      await completeChallenge(challenge.id, imageFile, notes);
      
      if (onComplete) {
        onComplete(challenge);
      }

      setShowCompletionModal(false);
      onClose();
    } catch (error) {
      console.error('Error completing challenge:', error);
      setError('Failed to complete challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!challenge) return;
    
    try {
      setLoading(true);
      if (isParticipating) {
        await onQuit(challenge.id);
        setIsParticipating(false);
        onClose();
      } else {
        await onJoin(challenge.id);
        setIsParticipating(true);
      }
    } catch (error) {
      console.error('Error with challenge action:', error);
    } finally {
      setLoading(false);
      setShowQuitConfirm(false);
    }
  };

  const handleShareResults = () => {
    window.open('https://www.facebook.com/groups/1875669956357222/', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
        <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl text-white font-brand font-light italic">{challenge.title}</h2>
                  {isCompleted && userParticipant?.completedAt && (
                    <p className="text-sm text-white/70 mt-1">
                      Completed {new Date(userParticipant.completedAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <button 
                  onClick={onClose}
                  className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="gap-6 p-6">
              {/* Left column - Challenge info */}
              <div className="space-y-4">
                <h3 className="font-medium text-white mb-2">About this Challenge</h3>
                <div className="p-4 bg-white/10 rounded-xl">
                  <p className="text-white/80 text-sm">{challenge.description}</p>
                </div>

                {/* Requirements */}
                <div className="grid grid-col-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="p-4 bg-white/10 rounded-xl">
                    <h4 className="text-white mb-2">Flour</h4>
                    <ul className="space-y-1 text-sm text-white/80">
                      {challenge.requirements.flour.map((flour, index) => (
                        <li key={index}>{flour}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-white/10 rounded-xl">
                    <h4 className="text-white mb-2">Equipment</h4>
                    <ul className="space-y-1 text-sm text-white/80">
                      {challenge.requirements.equipment.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-white/10 rounded-xl">
                    <h4 className="text-white mb-2">Techniques</h4>
                    <ul className="space-y-1 text-sm text-white/80">
                      {challenge.requirements.technique.map((technique, index) => (
                        <li key={index}>{technique}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right column - Steps */}
              <div className="space-y-4 py-6">
                <h3 className="font-medium text-white">Challenge Steps</h3>
                <div className="space-y-4 text-sm">
                  {challenge.steps.map((step, index) => (
                    <div key={step.id} className="p-4 bg-white/10 rounded-xl">
                      <h4 className="font-medium text-white">Step {index + 1}: {step.title}</h4>
                      <p className="text-sm text-white/80 mt-2">{step.description}</p>
                      {step.tips.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-amber-400">Tips:</h5>
                          <ul className="mt-1 space-y-1">
                            {step.tips.map((tip, tipIndex) => (
                              <li key={tipIndex} className="text-sm text-white/80">
                                • {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Completion Result */}
            {isCompleted && userParticipant?.result && (
              <div className="p-6 bg-white/10 space-y-4">
                <h4 className="font-medium text-white">Your Result</h4>
                <div className="relative aspect-video rounded-lg overflow-hidden">
                  <img
                    src={userParticipant.result.imageUrl}
                    alt="Challenge completion"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                {userParticipant.result.notes && (
                  <div>
                    <h5 className="text-sm font-medium text-white/90 mb-2">Your Notes</h5>
                    <p className="text-sm text-white/80 whitespace-pre-line">
                      {userParticipant.result.notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="p-6 border-t border-white/10 bg-white/5">
              <div className="flex justify-between items-center">
                {/* Left side buttons - Admin actions */}
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="flex items-center gap-2 px-6 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all duration-300"
                    >
                      <Edit className="hidden sm:block w-4 h-4" />
                      Edit Challenge
                    </button>
                  )}
                </div>

                {/* Right side buttons */}
                <div className="flex gap-3 justify-between items-center">
                  {isCompleted ? (
                    // Show only Results and Share buttons for completed challenges
                    <>
                      <button
                        onClick={() => setShowResultsModal(true)}
                        className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all duration-300"
                      >
                        <Eye className="w-4 h-4" />
                        All Results
                      </button>
                      <button
                        onClick={handleShareResults}
                        className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl transition-all duration-300"
                      >
                        <Share2 className="w-4 h-4" />
                        Share
                      </button>
                    </>
                  ) : (
                    // Show action buttons for non-completed challenges
                    <>
                      {showQuitConfirm ? (
                        // Quit confirmation buttons
                        <>
                          <button
                            onClick={() => setShowQuitConfirm(false)}
                            className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                          >
                            Keep Challenge
                          </button>
                          <button
                            onClick={handleAction}
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-xl transition-all duration-300"
                          >
                            {loading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Quitting...
                              </>
                            ) : (
                              <>
                                <Ban className="w-4 h-4" />
                                Confirm Quit
                              </>
                            )}
                          </button>
                        </>
                      ) : (
                        // Normal action buttons
                        <>
                          {isParticipating && !isCompleted && (
                            <>
                              <button
                                onClick={() => setShowQuitConfirm(true)}
                                className="flex items-center gap-2 px-6 py-2 bg-red-500/20 text-red-200 rounded-xl hover:bg-red-500/30 transition-all duration-300"
                              >
                                <Ban className="w-4 h-4" />
                                Quit Challenge
                              </button>
                              <button
                                onClick={() => setShowCompletionModal(true)}
                                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
                              >
                                <Upload className="w-4 h-4" />
                                Complete Challenge
                              </button>
                            </>
                          )}
                          {!isParticipating && !isAdmin && (
                            <button
                              onClick={handleAction}
                              disabled={loading}
                              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
                            >
                              {loading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Joining...
                                </>
                              ) : (
                                <>
                                  Join Challenge
                                  <ChevronRight className="w-4 h-4" />
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCompletionModal && (
        <ChallengeCompletionModal
          challenge={challenge}
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          onComplete={handleCompleteSubmit}
        />
      )}

      {showEditModal && (
        <EditChallengeModal
          challenge={challenge}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={() => {
            setShowEditModal(false);
            // Optionally refresh the challenge data here
          }}
        />
      )}

      <ChallengeResultsModal
        isOpen={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        challenge={challenge}
      />
    </>
  );
}